'use client';

import { c as _c } from "react/compiler-runtime";
import { BasicTooltip } from '@/comps/basic-tooltip';
import { PagesResizableSheet } from '@/domains/pages/shared/pages-resizable-sheet';
import { CreateProgramButton } from '@/domains/pages/ai-proficiency/components/create-program-button';
import { DimensionPowerBar } from '@/domains/pages/ai-proficiency/components/dimension-power-bar';
import { LevelShareDonut } from '@/domains/pages/ai-proficiency/components/level-share-donut';
import { AIFI_LEVEL_COLORS, AIFI_NEGATIVE_COLOR, AIFI_POSITIVE_TEXT_COLOR, DIMENSION_SCORE_CEILING, METHODOLOGY_DIMENSIONS, type AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { getDimensionTooltip } from '@/domains/pages/ai-proficiency/copy/dimension-tooltips';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import type { LevelDimensionTargets } from '@/domains/pages/ai-proficiency/fixtures/upskilling-benchmarks';
import type { MethodologyDimension } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import type { LevelSnapshot } from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import { rankDimensionsByLowestScore } from '@/domains/pages/ai-proficiency/utils/upskilling';
import { formatUserCount } from '@/domains/pages/ai-proficiency/utils/format-users';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { Info, Users } from 'lucide-react';
import { useMemo } from 'react';
type UpskillingDetailsDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level: AifiLevel | null;
  snapshot: LevelSnapshot | undefined;
  groupDimensions: MethodologyDimension[];
  orgDimensions: MethodologyDimension[];
  levelTargets: LevelDimensionTargets;
  scopeLabel: string;
  onCreateProgram: (dimensionKey: string, dimensionLabel: string) => void;
};
export function UpskillingDetailsDrawer(t0) {
  const $ = _c(55);
  const {
    open,
    onOpenChange,
    level,
    snapshot,
    groupDimensions,
    orgDimensions,
    levelTargets,
    scopeLabel,
    onCreateProgram
  } = t0;
  const color = level ? AIFI_LEVEL_COLORS[level] : "#fff";
  const userCount = snapshot?.userCount ?? 0;
  const sharePercent = snapshot?.sharePercent ?? 0;
  let t1;
  if ($[0] !== groupDimensions) {
    t1 = rankDimensionsByLowestScore(groupDimensions);
    $[0] = groupDimensions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ranked = t1;
  let t2;
  if ($[2] !== orgDimensions) {
    t2 = Object.fromEntries(orgDimensions.map(_temp));
    $[2] = orgDimensions;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const orgByKey = t2;
  if (!level) {
    return null;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("line-clamp-1 shrink-0 text-[13px] tracking-[0.18em] uppercase", AIFI_DATA_NUMBER_CLASS);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== color) {
    t4 = {
      color
    };
    $[5] = color;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== level || $[8] !== t4) {
    t5 = <h2 className={t3} style={t4}>{level}</h2>;
    $[7] = level;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `the ${scopeLabel}`;
  let t7;
  if ($[10] !== color || $[11] !== level || $[12] !== sharePercent || $[13] !== t6 || $[14] !== userCount) {
    t7 = <LevelShareDonut sharePercent={sharePercent} color={color} label={level} userCount={userCount} shareOf={t6} sizePx={24} />;
    $[10] = color;
    $[11] = level;
    $[12] = sharePercent;
    $[13] = t6;
    $[14] = userCount;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== sharePercent) {
    t8 = formatScore(sharePercent, {
      decimals: 1
    });
    $[16] = sharePercent;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <span className={AIFI_DATA_NUMBER_CLASS}>{t8}%</span>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== scopeLabel || $[21] !== t9) {
    t10 = <span className="min-w-0">{t9} of the{" "}{scopeLabel}</span>;
    $[20] = scopeLabel;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span aria-hidden={true}>·</span>;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Users className="size-3.5 shrink-0" strokeWidth={2.25} aria-hidden={true} />;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== userCount) {
    t13 = formatUserCount(userCount);
    $[25] = userCount;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <span className="flex shrink-0 items-center gap-1.5">{t12}<span className={AIFI_DATA_NUMBER_CLASS}>{t13}</span></span>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t14 || $[31] !== t7) {
    t15 = <span className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">{t7}{t10}{t11}{t14}</span>;
    $[29] = t10;
    $[30] = t14;
    $[31] = t7;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t15 || $[34] !== t5) {
    t16 = <div className="flex shrink-0 flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-b-lightborder px-5 py-3">{t5}{t15}</div>;
    $[33] = t15;
    $[34] = t5;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="shrink-0 px-5 pt-3"><p className="text-sm leading-relaxed text-muted-foreground">The six behaviors used to calculate your AI proficiency score, ranked from weakest to strongest. Work on the top two to move this group to the next level fastest.</p></div>;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== levelTargets || $[38] !== onCreateProgram || $[39] !== orgByKey || $[40] !== ranked) {
    let t19;
    if ($[42] !== levelTargets || $[43] !== onCreateProgram || $[44] !== orgByKey) {
      t19 = dimension_0 => {
        const orgAvg = orgByKey[dimension_0.key] ?? 0;
        const target = levelTargets[dimension_0.key] ?? DIMENSION_SCORE_CEILING;
        const vsTarget = dimension_0.score - target;
        const isPriority = dimension_0.rank <= 2;
        const meta = METHODOLOGY_DIMENSIONS.find(item => item.key === dimension_0.key);
        const label = meta?.label ?? dimension_0.label;
        const description = meta?.description ?? dimension_0.description;
        const tooltip = getDimensionTooltip(dimension_0.key, description);
        return <div key={dimension_0.key} className={cn(isPriority ? "rounded-md border px-3 py-2.5" : "px-3 py-2")} style={isPriority ? {
          borderColor: `${AIFI_NEGATIVE_COLOR}66`,
          backgroundColor: `${AIFI_NEGATIVE_COLOR}0D`
        } : undefined}><div className="flex items-start justify-between gap-2"><div className="flex min-w-0 items-center gap-1.5">{isPriority ? <span className={cn("flex size-4 shrink-0 items-center justify-center rounded border border-border text-[9px] font-semibold", AIFI_DATA_NUMBER_CLASS)}>{dimension_0.rank}</span> : null}<span className="size-1.5 shrink-0 rounded-full" style={{
                backgroundColor: dimension_0.color
              }} /><span className="text-xs font-medium text-foreground">{label}</span></div><div className="flex shrink-0 items-baseline gap-2"><span className={cn("text-xs font-semibold text-foreground", AIFI_DATA_NUMBER_CLASS)}>{formatScore(dimension_0.score, {
                  decimals: 0
                })}/100</span><span className={cn("text-[10px]", AIFI_DATA_NUMBER_CLASS)} style={{
                color: vsTarget >= 0 ? AIFI_POSITIVE_TEXT_COLOR : AIFI_NEGATIVE_COLOR
              }}>{vsTarget >= 0 ? "+" : ""}{formatScore(vsTarget, {
                  decimals: 0
                })} vs. target</span></div></div><div className="mt-1"><DimensionPowerBar score={dimension_0.score} orgAvg={orgAvg} ceiling={target} fillColor={dimension_0.color} compact={true} /></div><div className="mt-1.5 flex items-start justify-between gap-3"><p className="min-w-0 flex-1 text-[11px] leading-snug text-muted-foreground">{description}{tooltip ? <BasicTooltip text={tooltip} side="bottom" classNameContent="max-w-80 break-normal leading-relaxed"><button type="button" aria-label={`About ${label.toLowerCase()}`} className="ml-1 inline-flex cursor-default align-[-0.1em] text-muted-foreground outline-hidden transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"><Info className="size-3" aria-hidden={true} /></button></BasicTooltip> : null}</p><CreateProgramButton size="xs" className="shrink-0" onClick={() => onCreateProgram(dimension_0.key, label)} /></div></div>;
      };
      $[42] = levelTargets;
      $[43] = onCreateProgram;
      $[44] = orgByKey;
      $[45] = t19;
    } else {
      t19 = $[45];
    }
    t18 = ranked.map(t19);
    $[37] = levelTargets;
    $[38] = onCreateProgram;
    $[39] = orgByKey;
    $[40] = ranked;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[46] !== t18) {
    t19 = <div className="mt-4 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-5 pb-5">{t18}</div>;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t16 || $[49] !== t19) {
    t20 = <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{t16}{t17}{t19}</div>;
    $[48] = t16;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== onOpenChange || $[52] !== open || $[53] !== t20) {
    t21 = <PagesResizableSheet open={open} onOpenChange={onOpenChange}>{t20}</PagesResizableSheet>;
    $[51] = onOpenChange;
    $[52] = open;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  return t21;
}
function _temp(dimension) {
  return [dimension.key, dimension.score];
}